import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { createLogger } from "@ict/shared";
import { useToast } from "@/contexts/ToastContext";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useCachedMachineDocument } from "@/hooks/useCachedMachineDocument";
import { OfflineBanner } from "@/components/ui/OfflineBanner";
import { ChecklistTab } from "@/components/inspection/ChecklistTab";
import { MeasureTab } from "@/components/inspection/MeasureTab";
import { SummaryTab, type SummaryTabMachineInfo } from "@/components/inspection/SummaryTab";
import { OtherPhotosTab } from "@/components/inspection/OtherPhotosTab";
import {
  UPDATE_MACHINE_DOCUMENT_ITEMS_MUTATION,
  UPDATE_MACHINE_INFO_MUTATION,
  MachineInfoField,
  type MachineInfoPatch,
} from "@/lib/graphql/inspection";
import { MACHINE_DOCUMENTS_QUERY } from "@/lib/graphql/equipment";
import {
  MACHINE_TITLES,
  type MachineKey,
  getInspectionTypeFields,
  MACHINE_INFO_ERROR_CODES,
} from "@ict/shared/constants";
import type { CheckListItem, CheckResultValue } from "@/types";
import { addToSyncQueue, getPendingSyncCount } from "@/lib/db/changes";
import { getCachedMachine, cacheMachine, getCachedMachinesByDocKey } from "@/lib/db/machines";
// 참고: useAutoSync는 Layout에서 전역으로 처리됨

const logger = createLogger({ module: "InspectionPage" });

/** 설비 정보 로컬 상태 타입 (SummaryTabMachineInfo 확장) */
interface LocalMachineInfo extends SummaryTabMachineInfo {
  id: string;
  version: number;
}

// 탭 구조: 요약 | 점검 | 사진대지 | 계측 (4개)
type TabType = "summary" | "checklist" | "other-photos" | "measure";

// 탭 순서 (스와이프 네비게이션용)
const TAB_ORDER: TabType[] = ["summary", "checklist", "other-photos", "measure"];

// 스크롤 애니메이션 지속 시간 (ms)
const SCROLL_ANIMATION_DURATION = 300;

export function InspectionPage() {
  const { projectId, docKey, machineId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // 오프라인 상태 확인
  const { isOffline } = useNetworkStatus();

  // 동기화 대기 중인 변경사항 수
  const [pendingChanges, setPendingChanges] = useState(0);

  const [activeTab, setActiveTab] = useState<TabType>("summary");
  const [localCheckList, setLocalCheckList] = useState<CheckListItem[]>([]);

  // 탭 스크롤 컨테이너 ref
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);

  // 탭 스와이프 제어용 ref (최대 1탭 이동 제한)
  const touchStartXRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);
  const currentTabIndexRef = useRef<number>(0);
  // 터치 방향 결정 플래그: null=미결정, "horizontal"=수평, "vertical"=수직
  const touchDirectionRef = useRef<"horizontal" | "vertical" | null>(null);

  const [localMachineInfo, setLocalMachineInfo] = useState<LocalMachineInfo | null>(null);
  const [pendingMachineInfoChanges, setPendingMachineInfoChanges] = useState<
    Record<string, string>
  >({});
  const [documentVersion, setDocumentVersion] = useState(0);

  // 사진대지 로컬 상태
  const [localPictures, setLocalPictures] = useState<string[]>([]);

  // GraphQL 쿼리 (오프라인 지원)
  const { project, machineDocument, loading, error, refetch } = useCachedMachineDocument({
    projectId: projectId || "",
    docKey: docKey || "",
    machineId,
  });

  // 온라인 전환 시 서버 데이터 refetch
  const wasOfflineRef = useRef<boolean | null>(null);
  useEffect(() => {
    // 이전 상태가 오프라인이었고, 현재 온라인이면 refetch
    if (wasOfflineRef.current === true && isOffline === false) {
      // 상태 즉시 업데이트 (중복 refetch 방지)
      wasOfflineRef.current = isOffline;
      // 동기화 완료 대기 후 refetch (동기화가 먼저 완료되도록)
      const timer = setTimeout(() => {
        refetch();
      }, 2000); // 동기화 완료 대기
      return () => clearTimeout(timer);
    }
    wasOfflineRef.current = isOffline;
  }, [isOffline, refetch]);

  // 프로젝트 타입에 따른 필드 정보 (성능점검: checkList, 유지관리: maintenance)
  const inspectionFields = useMemo(() => getInspectionTypeFields(project?.type), [project?.type]);

  // machineId 변경 시 로컬 상태 즉시 초기화 (깜빡임 방지, 버전 충돌 방지)
  useEffect(() => {
    setLocalCheckList([]);
    setLocalMachineInfo(null);
    setPendingMachineInfoChanges({});
    setLocalPictures([]);
    setDocumentVersion(0); // 버전도 초기화하여 stale version 사용 방지
  }, [machineId]);

  // 동기화 대기 카운트 업데이트
  useEffect(() => {
    const updatePendingCount = async () => {
      const count = await getPendingSyncCount();
      setPendingChanges(count);
    };
    updatePendingCount();
  }, [localCheckList, localMachineInfo]); // 로컬 상태 변경 시 업데이트

  // 올바른 문서 데이터가 도착하면 로컬 상태 동기화
  useEffect(() => {
    // 현재 machineId와 일치하는 데이터만 처리
    if (!machineDocument || machineDocument.id !== machineId) {
      return;
    }

    // 프로젝트 타입에 따라 사용할 필드 선택 (성능점검: checkList, 유지관리: maintenance)
    const sourceArray =
      inspectionFields.checkListField === "maintenance"
        ? machineDocument.maintenance
        : machineDocument.checkList;

    // API 응답을 프론트엔드 타입으로 매핑
    // API: id, title, result, group, image, imageO, imageX, underReason, underSolution
    // → 프론트: _id, item, result, category, image, imageO, imageX, underReason, underSolution
    const mappedCheckList: CheckListItem[] = (sourceArray || []).map(
      (item: {
        id: string;
        title: string;
        result?: string;
        group?: string;
        image?: string;
        imageO?: string;
        imageX?: string;
        underReason?: string;
        underSolution?: string;
      }) => ({
        _id: item.id,
        item: item.title,
        result: (item.result as CheckResultValue) || null,
        category: item.group || "기타",
        image: item.image,
        imageO: item.imageO,
        imageX: item.imageX,
        underReason: item.underReason,
        underSolution: item.underSolution,
      })
    );

    // 설비 정보 매핑
    const mappedMachineInfo: LocalMachineInfo = {
      id: machineDocument.id,
      modelNo: machineDocument.modelNo || "",
      checkName: machineDocument.checkName || "",
      checkStartDate: machineDocument.checkStartDate || "",
      checkEndDate: machineDocument.checkEndDate || "",
      modelYear: machineDocument.modelYear || "",
      position: machineDocument.position || "",
      modelCreator: machineDocument.modelCreator || "",
      version: machineDocument.version || 0,
    };

    setLocalCheckList(mappedCheckList);
    setLocalMachineInfo(mappedMachineInfo);
    setDocumentVersion(machineDocument.version || 0);
    setLocalPictures(machineDocument.pictures || []);
  }, [machineDocument, machineId, inspectionFields]);

  // 같은 설비 타입의 문서 목록 조회 (네비게이션용)
  // 온라인: GraphQL 쿼리, 오프라인: IndexedDB 캐시
  const { data: documentsData } = useQuery(MACHINE_DOCUMENTS_QUERY, {
    variables: { projectId, docKey },
    skip: !projectId || !docKey || isOffline,
  });

  // 오프라인용 캐시 데이터
  const [cachedDocuments, setCachedDocuments] = useState<
    Array<{ id: string; title: string; position?: number }>
  >([]);

  useEffect(() => {
    if (!projectId || !docKey || !isOffline) return;

    const loadCachedDocuments = async () => {
      const machines = await getCachedMachinesByDocKey(projectId, docKey);
      const docs = machines.map((m) => ({
        id: m.machine._id,
        title: m.machine.name || "",
        position: undefined as number | undefined,
      }));
      setCachedDocuments(docs);
    };

    loadCachedDocuments();
  }, [projectId, docKey, isOffline]);

  // 문서 네비게이션 상태 계산 (온라인/오프라인 데이터 통합)
  const navigation = useMemo(() => {
    const documents = isOffline ? cachedDocuments : documentsData?.machineDocuments || [];
    const currentIndex = documents.findIndex((doc: { id: string }) => doc.id === machineId);
    return {
      documents,
      currentIndex: currentIndex >= 0 ? currentIndex : 0,
      totalCount: documents.length,
      hasPrev: currentIndex > 0,
      hasNext: currentIndex < documents.length - 1,
    };
  }, [isOffline, cachedDocuments, documentsData?.machineDocuments, machineId]);

  const { documents, currentIndex, totalCount, hasPrev, hasNext } = navigation;

  // 데이터 stale 여부 (로딩 중이거나 현재 machineId와 불일치)
  const isDataStale = loading || machineDocument?.id !== machineId;

  const handleDocumentNavigation = useCallback(
    (offset: -1 | 1) => {
      const target = documents[currentIndex + offset];
      if (!target) return;
      navigate(`/projects/${projectId}/inspection/${docKey}/${target.id}`, { replace: true });
    },
    [documents, currentIndex, navigate, projectId, docKey]
  );

  // Mutations
  const [updateMachineDocumentItems] = useMutation(UPDATE_MACHINE_DOCUMENT_ITEMS_MUTATION);
  const [updateMachineInfo, { loading: savingMachineInfo }] = useMutation(
    UPDATE_MACHINE_INFO_MUTATION
  );

  // 에러 메시지 상태
  const [saveError, setSaveError] = useState<string | null>(null);
  const [machineInfoError, setMachineInfoError] = useState<string | null>(null);

  // 저장 중인 항목 ID (개별 로딩 표시용)
  const [savingItemId, setSavingItemId] = useState<string | null>(null);

  // 사진/원인/조치 변경 핸들러 (통합된 ChecklistTab에서 사용)
  const handlePhotoChange = useCallback(
    async (updatedItem: CheckListItem) => {
      if (!projectId || !docKey || !machineId || !updatedItem._id) return;

      // 롤백을 위해 이전 상태 저장
      const previousCheckList = localCheckList;

      // 로컬 상태 업데이트 (낙관적 업데이트)
      const newCheckList = localCheckList.map((item) =>
        item._id === updatedItem._id ? updatedItem : item
      );
      setLocalCheckList(newCheckList);
      setSaveError(null);

      // 변경된 필드만 패치로 생성
      const previousItem = previousCheckList.find((item) => item._id === updatedItem._id);
      const patches: Array<{
        itemId: string;
        itemType: string;
        field: string;
        value: string | null | undefined;
        oldValue?: string | null;
        itemTitle?: string;
      }> = [];

      // image, imageO, imageX, underReason, underSolution 필드만 비교하여 패치 생성
      if (previousItem?.image !== updatedItem.image) {
        patches.push({
          itemId: updatedItem._id,
          itemType: inspectionFields.itemType,
          field: "image",
          value: updatedItem.image ?? null,
          oldValue: previousItem?.image ?? null,
          itemTitle: updatedItem.item,
        });
      }
      if (previousItem?.imageO !== updatedItem.imageO) {
        patches.push({
          itemId: updatedItem._id,
          itemType: inspectionFields.itemType,
          field: "imageO",
          value: updatedItem.imageO ?? null,
          oldValue: previousItem?.imageO ?? null,
          itemTitle: updatedItem.item,
        });
      }
      if (previousItem?.imageX !== updatedItem.imageX) {
        patches.push({
          itemId: updatedItem._id,
          itemType: inspectionFields.itemType,
          field: "imageX",
          value: updatedItem.imageX ?? null,
          oldValue: previousItem?.imageX ?? null,
          itemTitle: updatedItem.item,
        });
      }
      if (previousItem?.underReason !== updatedItem.underReason) {
        patches.push({
          itemId: updatedItem._id,
          itemType: inspectionFields.itemType,
          field: "underReason",
          value: updatedItem.underReason ?? null,
          oldValue: previousItem?.underReason ?? null,
          itemTitle: updatedItem.item,
        });
      }
      if (previousItem?.underSolution !== updatedItem.underSolution) {
        patches.push({
          itemId: updatedItem._id,
          itemType: inspectionFields.itemType,
          field: "underSolution",
          value: updatedItem.underSolution ?? null,
          oldValue: previousItem?.underSolution ?? null,
          itemTitle: updatedItem.item,
        });
      }

      if (patches.length === 0) return;

      // 오프라인 상태: 로컬 저장 + 동기화 큐에 추가
      if (isOffline) {
        try {
          // IndexedDB 캐시 업데이트
          const cachedMachine = await getCachedMachine(projectId, docKey, machineId);
          if (cachedMachine) {
            const fieldName = inspectionFields.checkListField;
            const updatedData = {
              ...cachedMachine,
              [fieldName]: newCheckList.map((item) => ({
                _id: item._id,
                item: item.item,
                result: item.result,
                category: item.category,
                image: item.image,
                imageO: item.imageO,
                imageX: item.imageX,
                underReason: item.underReason,
                underSolution: item.underSolution,
              })),
              synced: false,
              syncStatus: "pending" as const,
              localUpdatedAt: Date.now(),
            };
            await cacheMachine(updatedData);
          }

          // 동기화 큐에 각 patch를 개별 항목으로 추가
          for (const patch of patches) {
            await addToSyncQueue({
              type: "checklist",
              entityId: machineId,
              operation: "update",
              payload: {
                patches: [
                  {
                    itemId: patch.itemId,
                    itemType: patch.itemType as "checkList" | "data" | "maintenance",
                    field: patch.field,
                    value: patch.value ?? null,
                    oldValue: patch.oldValue ?? null,
                    itemTitle: patch.itemTitle,
                  },
                ],
              },
              projectId,
              docKey,
              machineId,
              expectedVersion: documentVersion,
            });
          }

          showToast("오프라인 저장됨 (온라인 시 동기화)", "info");
        } catch (err) {
          logger.error("오프라인 저장 실패:", err);
          setLocalCheckList(previousCheckList);
          setSaveError("오프라인 저장에 실패했습니다.");
          setTimeout(() => setSaveError(null), 3000);
        }
        return;
      }

      // 온라인 상태: 서버에 저장
      try {
        const response = await updateMachineDocumentItems({
          variables: {
            input: {
              projectId,
              docKey,
              machineId,
              expectedVersion: documentVersion,
              clientMutationId: `mobile-photo-${Date.now()}`,
              patches: patches.map(({ itemId, itemType, field, value }) => ({
                itemId,
                itemType,
                field,
                value,
              })),
            },
          },
        });

        if (response.data?.updateMachineDocumentItems?.newVersion) {
          setDocumentVersion(response.data.updateMachineDocumentItems.newVersion);
          showToast("점검사진이 저장되었습니다", "success");
        }
      } catch (err) {
        logger.error("점검사진 저장 실패:", err);
        setLocalCheckList(previousCheckList);
        setSaveError("저장에 실패했습니다. 다시 시도해 주세요.");
        setTimeout(() => setSaveError(null), 3000);
      }
    },
    [
      updateMachineDocumentItems,
      projectId,
      docKey,
      machineId,
      localCheckList,
      documentVersion,
      inspectionFields,
      showToast,
      isOffline,
    ]
  );

  // 점검항목 결과 변경 및 즉시 저장
  const handleChecklistChange = useCallback(
    async (updatedItem: CheckListItem) => {
      // 필수 파라미터 검증
      if (!projectId || !docKey || !machineId || !updatedItem._id) return;

      // 이미 저장 중인 항목이면 무시 (연속 터치 방지)
      if (savingItemId === updatedItem._id) return;

      // 롤백을 위해 이전 상태 저장
      const previousCheckList = localCheckList;
      const previousItem = localCheckList.find((item) => item._id === updatedItem._id);

      // 로컬 상태 업데이트 (낙관적 업데이트)
      const newCheckList = localCheckList.map((item) =>
        item._id === updatedItem._id ? updatedItem : item
      );
      setLocalCheckList(newCheckList);
      setSaveError(null);

      // 오프라인 상태: 로컬 저장 + 동기화 큐에 추가
      if (isOffline) {
        try {
          // IndexedDB 캐시 업데이트
          const cachedMachine = await getCachedMachine(projectId, docKey, machineId);
          if (cachedMachine) {
            // checkList 또는 maintenance 필드 업데이트
            const fieldName = inspectionFields.checkListField;
            const updatedData = {
              ...cachedMachine,
              [fieldName]: newCheckList.map((item) => ({
                _id: item._id,
                item: item.item,
                result: item.result,
                category: item.category,
                image: item.image,
                imageO: item.imageO,
                imageX: item.imageX,
                underReason: item.underReason,
                underSolution: item.underSolution,
              })),
              synced: false,
              syncStatus: "pending" as const,
              localUpdatedAt: Date.now(),
            };
            await cacheMachine(updatedData);
          }

          // 동기화 큐에 추가
          await addToSyncQueue({
            type: "checklist",
            entityId: machineId,
            operation: "update",
            payload: {
              patches: [
                {
                  itemId: updatedItem._id,
                  itemType: inspectionFields.itemType as "checkList" | "data" | "maintenance",
                  field: "result",
                  value: updatedItem.result,
                  oldValue: previousItem?.result ?? null,
                  itemTitle: updatedItem.item,
                },
              ],
            },
            projectId,
            docKey,
            machineId,
            expectedVersion: documentVersion,
          });

          showToast("오프라인 저장됨 (온라인 시 동기화)", "info");
        } catch (err) {
          logger.error("오프라인 저장 실패:", err);
          setLocalCheckList(previousCheckList);
          setSaveError("오프라인 저장에 실패했습니다.");
          setTimeout(() => setSaveError(null), 3000);
        }
        return;
      }

      // 온라인 상태: 서버에 저장
      // 저장 중 상태 설정
      setSavingItemId(updatedItem._id);

      try {
        const response = await updateMachineDocumentItems({
          variables: {
            input: {
              projectId,
              docKey,
              machineId,
              expectedVersion: documentVersion,
              clientMutationId: `mobile-${Date.now()}`,
              patches: [
                {
                  itemId: updatedItem._id,
                  itemType: inspectionFields.itemType,
                  field: "result",
                  value: updatedItem.result,
                },
              ],
            },
          },
        });

        // 새 버전으로 업데이트
        if (response.data?.updateMachineDocumentItems?.newVersion) {
          setDocumentVersion(response.data.updateMachineDocumentItems.newVersion);
          showToast("점검결과가 저장되었습니다", "success");
        }
      } catch (err) {
        logger.error("점검항목 저장 실패:", err);

        // 로컬 상태 롤백
        if (previousItem) {
          setLocalCheckList(previousCheckList);
        }

        // 사용자에게 에러 알림
        setSaveError("저장에 실패했습니다. 다시 시도해 주세요.");

        // 3초 후 에러 메시지 자동 제거
        setTimeout(() => setSaveError(null), 3000);
      } finally {
        // 저장 완료
        setSavingItemId(null);
      }
    },
    [
      updateMachineDocumentItems,
      projectId,
      docKey,
      machineId,
      localCheckList,
      documentVersion,
      inspectionFields,
      savingItemId,
      showToast,
      isOffline,
    ]
  );

  // 설비 정보 초기값 (oldValue 추적용)
  const initialMachineInfoRef = useRef<LocalMachineInfo | null>(null);

  // localMachineInfo 초기 로드 시 initialMachineInfoRef 업데이트
  useEffect(() => {
    if (localMachineInfo && !initialMachineInfoRef.current) {
      initialMachineInfoRef.current = { ...localMachineInfo };
    }
  }, [localMachineInfo]);

  // machineId 변경 시 초기값 초기화
  useEffect(() => {
    initialMachineInfoRef.current = null;
  }, [machineId]);

  // 설비 정보 변경 핸들러 (로컬 상태 업데이트)
  const handleMachineInfoChange = useCallback((field: string, value: string | undefined) => {
    // 변경 사항을 pending 상태에 저장
    setPendingMachineInfoChanges((prev) => ({
      ...prev,
      [field]: value ?? "",
    }));
    // 로컬 상태 즉시 업데이트 (UI 반영)
    setLocalMachineInfo((prev) =>
      prev
        ? {
            ...prev,
            [field]: value ?? "",
          }
        : null
    );
  }, []);

  // 설비 정보 blur 핸들러 (자동 저장)
  // immediateChange: onFieldChange 직후 호출 시 state 반영 전이므로 직접 전달받음
  const handleMachineInfoBlur = useCallback(
    async (
      immediateChange?: { field: string; value: string } | { field: string; value: string }[]
    ) => {
      // 변경된 필드를 병합 (immediate + pending)
      let allChanges = { ...pendingMachineInfoChanges };
      if (immediateChange) {
        const changes = Array.isArray(immediateChange) ? immediateChange : [immediateChange];
        for (const change of changes) {
          allChanges[change.field] = change.value;
        }
      }

      // 변경된 필드가 없으면 저장하지 않음
      if (Object.keys(allChanges).length === 0) return;
      if (!projectId || !docKey || !machineId || !localMachineInfo) return;

      // 변경된 필드만 patches로 변환 (oldValue 포함)
      const patches: (MachineInfoPatch & { oldValue?: string })[] = Object.entries(allChanges)
        .filter(([, value]) => value !== undefined)
        .map(([field, value]) => {
          // 초기값에서 oldValue 가져오기 (타입 안전하게)
          let oldValue: string | undefined;
          if (initialMachineInfoRef.current) {
            const initialInfo = initialMachineInfoRef.current;
            const fieldKey = field as keyof LocalMachineInfo;
            // id와 version을 제외한 string 필드만 추출
            if (
              fieldKey !== "id" &&
              fieldKey !== "version" &&
              typeof initialInfo[fieldKey] === "string"
            ) {
              oldValue = initialInfo[fieldKey] as string;
            }
          }
          return {
            field: field as MachineInfoField,
            value: value,
            oldValue: oldValue ?? "",
          };
        });

      if (patches.length === 0) return;

      // 오프라인 상태: 로컬 저장 + 동기화 큐에 추가
      if (isOffline) {
        try {
          // IndexedDB 캐시 업데이트
          const cachedMachine = await getCachedMachine(projectId, docKey, machineId);
          if (cachedMachine) {
            // UI 필드명 → 캐시 필드명 매핑 (필드명 불일치 수정)
            // useCachedMachineDocument에서 load 시 machine.model → modelNo, machine.manufacturer → modelCreator 로 변환하므로
            // save 시에도 동일하게 역변환해야 함
            const fieldNameMap: Record<string, string> = {
              modelNo: "model",
              modelCreator: "manufacturer",
            };
            const machineUpdates: Record<string, unknown> = {};
            for (const patch of patches) {
              const cacheFieldName = fieldNameMap[patch.field] || patch.field;
              machineUpdates[cacheFieldName] = patch.value;
            }
            const updatedData = {
              ...cachedMachine,
              machine: {
                ...cachedMachine.machine,
                ...machineUpdates,
              },
              synced: false,
              syncStatus: "pending" as const,
              localUpdatedAt: Date.now(),
            };
            await cacheMachine(updatedData);
          }

          // 동기화 큐에 각 patch를 개별 항목으로 추가
          for (const patch of patches) {
            await addToSyncQueue({
              type: "machine",
              entityId: machineId,
              operation: "update",
              payload: {
                machineInfoPatches: [
                  {
                    field: patch.field,
                    value: patch.value,
                    oldValue: patch.oldValue,
                  },
                ],
              },
              projectId,
              docKey,
              machineId,
              expectedVersion: localMachineInfo.version,
            });
          }

          showToast("오프라인 저장됨 (온라인 시 동기화)", "info");
          // pending 상태 초기화
          setPendingMachineInfoChanges({});
          // 초기값 업데이트 (다음 변경 추적용)
          if (localMachineInfo) {
            initialMachineInfoRef.current = { ...localMachineInfo };
          }
        } catch (err) {
          logger.error("오프라인 저장 실패:", err);
          setMachineInfoError("오프라인 저장에 실패했습니다.");
          setTimeout(() => setMachineInfoError(null), 3000);
        }
        return;
      }

      // 온라인 상태: 서버에 저장
      setMachineInfoError(null);

      try {
        const response = await updateMachineInfo({
          variables: {
            input: {
              projectId,
              docKey,
              machineId,
              expectedVersion: localMachineInfo.version,
              clientMutationId: `mobile-${Date.now()}`,
              // GraphQL MachineInfoPatchInput에 맞게 oldValue 제거
              patches: patches.map(({ field, value }) => ({ field, value })),
            },
          },
        });

        // 성공 시 새 버전으로 업데이트
        if (response.data?.updateMachineInfo?.success) {
          const newVersion = response.data.updateMachineInfo.newVersion;
          if (newVersion !== undefined) {
            setLocalMachineInfo((prev) =>
              prev
                ? {
                    ...prev,
                    version: newVersion,
                  }
                : null
            );
            setDocumentVersion(newVersion);
          }
          showToast("설비 정보가 저장되었습니다", "success");
          // pending 상태 초기화
          setPendingMachineInfoChanges({});
          // 초기값 업데이트 (다음 변경 추적용)
          if (localMachineInfo) {
            initialMachineInfoRef.current = {
              ...localMachineInfo,
              version: newVersion,
            };
          }
        } else {
          // 실패 시 errorCode 기반 구체적 에러 메시지 표시
          const errorCode = response.data?.updateMachineInfo?.errorCode;
          const errorMsg = response.data?.updateMachineInfo?.error;

          if (errorCode === MACHINE_INFO_ERROR_CODES.VERSION_CONFLICT) {
            setMachineInfoError("다른 곳에서 수정되었습니다. 새로고침 후 다시 시도해주세요.");
          } else {
            setMachineInfoError(errorMsg || "저장에 실패했습니다. 다시 시도해 주세요.");
          }
          setTimeout(() => setMachineInfoError(null), 3000);
        }
      } catch (err) {
        logger.error("설비 정보 저장 실패:", err);
        setMachineInfoError("저장에 실패했습니다. 다시 시도해 주세요.");

        // 3초 후 에러 메시지 자동 제거
        setTimeout(() => setMachineInfoError(null), 3000);
      }
    },
    [
      pendingMachineInfoChanges,
      projectId,
      docKey,
      machineId,
      localMachineInfo,
      updateMachineInfo,
      showToast,
      isOffline,
    ]
  );

  // 사진대지 업데이트 핸들러
  const handlePicturesUpdate = useCallback((pictures: string[], newVersion: number) => {
    setLocalPictures(pictures);
    setDocumentVersion(newVersion);
  }, []);

  // 터치 시작 핸들러 (스와이프 감지용)
  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    touchDirectionRef.current = null; // 방향 미결정 상태로 초기화
  }, []);

  // 터치 이동 핸들러 (수직/수평 방향 결정)
  const handleTouchMove = useCallback((e: TouchEvent) => {
    // 이미 방향이 결정되었으면 무시
    if (touchDirectionRef.current !== null) return;

    const deltaX = Math.abs(e.touches[0].clientX - touchStartXRef.current);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartYRef.current);

    // 최소 10px 이동 후 방향 결정 (너무 작은 움직임은 무시)
    const directionThreshold = 10;
    if (deltaX < directionThreshold && deltaY < directionThreshold) return;

    // 수평 이동이 수직 이동보다 1.5배 이상 클 때만 수평 스와이프로 인식
    if (deltaX > deltaY * 1.5) {
      touchDirectionRef.current = "horizontal";
    } else {
      touchDirectionRef.current = "vertical";
    }
  }, []);

  // 터치 종료 핸들러 (스와이프 방향에 따라 +-1 탭 이동)
  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      // 이미 스크롤 애니메이션 중이면 무시
      if (isScrollingRef.current) return;

      // 수직 스크롤로 판정된 경우 탭 전환 안함
      if (touchDirectionRef.current === "vertical") return;

      // 방향 미결정(거의 안 움직임)이면 탭 전환 안함
      if (touchDirectionRef.current === null) return;

      // 계측 탭 활성 시 메인 탭 스와이프 비활성 (서브탭에서 처리)
      if (currentTabIndexRef.current === TAB_ORDER.indexOf("measure")) return;

      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartXRef.current - touchEndX;
      const threshold = 50; // 최소 스와이프 거리 (px)

      // 스와이프 거리가 임계값 미만이면 무시
      if (Math.abs(diff) < threshold) return;

      // 스와이프 방향: 왼쪽 스와이프(diff > 0) = 다음 탭(+1), 오른쪽 스와이프(diff < 0) = 이전 탭(-1)
      const direction = diff > 0 ? 1 : -1;
      const newIndex = Math.max(
        0,
        Math.min(TAB_ORDER.length - 1, currentTabIndexRef.current + direction)
      );

      // 현재 탭과 같으면 무시
      if (newIndex === currentTabIndexRef.current) return;

      // 새 탭으로 이동
      const newTab = TAB_ORDER[newIndex];
      if (newTab) {
        isScrollingRef.current = true;
        setActiveTab(newTab);
        currentTabIndexRef.current = newIndex;

        // CSS transition 완료 후 플래그 해제
        setTimeout(() => {
          isScrollingRef.current = false;
        }, SCROLL_ANIMATION_DURATION);
      }
    },
    [] // ref 기반으로 동작하므로 의존성 불필요
  );

  // 터치 이벤트 리스너 등록
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    container.addEventListener("touchmove", handleTouchMove, { passive: true });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // 탭 버튼 클릭 → 탭 전환
  const scrollToTab = useCallback((tab: TabType) => {
    const index = TAB_ORDER.indexOf(tab);
    isScrollingRef.current = true;
    setActiveTab(tab);
    currentTabIndexRef.current = index;

    setTimeout(() => {
      isScrollingRef.current = false;
    }, SCROLL_ANIMATION_DURATION);
  }, []);

  // --- 계단식 뒤로가기 전략 ---
  // MeasureTab 서브탭 상태 추적
  const isFirstSubTabRef = useRef(true);
  const resetMeasureSubTabRef = useRef<(() => void) | null>(null);

  const handleSubTabInfo = useCallback((info: { isFirstSubTab: boolean }) => {
    isFirstSubTabRef.current = info.isFirstSubTab;
  }, []);

  // 계단식 뒤로가기 핸들러
  const handleBack = useCallback(() => {
    // 1단계: 모달/다이얼로그 닫기 시도 (cancelable 커스텀 이벤트)
    const event = new CustomEvent("closeTopModal", { cancelable: true });
    const wasHandled = !document.dispatchEvent(event);
    if (wasHandled) return;

    // 2단계: MeasureTab 서브탭이 첫번째가 아니면 → 첫번째 서브탭으로
    if (activeTab === "measure" && !isFirstSubTabRef.current) {
      resetMeasureSubTabRef.current?.();
      isFirstSubTabRef.current = true; // 동기 업데이트 (연타 방지)
      return;
    }

    // 3단계: 메인탭이 첫번째가 아니면 → 첫번째 메인탭으로
    if (activeTab !== TAB_ORDER[0]) {
      scrollToTab(TAB_ORDER[0]);
      return;
    }

    // 4단계: 페이지 이탈 (프로젝트 상세로)
    navigate(-1);
  }, [activeTab, scrollToTab, navigate]);

  // Android 하드웨어 백버튼 리스너
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listener = App.addListener("backButton", () => {
      handleBack();
    });

    return () => {
      listener.then((l) => l.remove());
    };
  }, [handleBack]);

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4">
        <p className="text-red-500">데이터를 불러오는데 실패했습니다</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-blue-600">
          돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      {/* 오프라인/캐시 배너 */}
      <OfflineBanner pendingChanges={pendingChanges} />

      {/* 에러 토스트 */}
      {saveError && (
        <div
          className="fixed left-4 right-4 z-50"
          style={{ top: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}
        >
          <div className="mx-auto max-w-sm rounded-xl bg-[#F04452] px-4 py-3 text-center">
            <span className="text-sm font-medium text-white">{saveError}</span>
          </div>
        </div>
      )}

      {/* 상단 고정 영역: 헤더 + 탭 바 */}
      <div className="sticky top-0 z-20 flex-shrink-0">
        {/* 헤더 - Industrial Modern 슬레이트 다크 */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 py-3 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full text-white/80 active:bg-white/10"
                aria-label="뒤로가기"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <div className="flex flex-col gap-0.5">
                {isDataStale ? (
                  // 스켈레톤 로딩 상태
                  <div className="animate-pulse">
                    <div className="h-4 w-24 rounded bg-white/20 mb-1" />
                    <div className="flex items-center gap-1.5">
                      <div className="h-3 w-16 rounded bg-white/15" />
                      <div className="h-4 w-10 rounded bg-white/20" />
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="text-[15px] font-semibold text-white">
                      {project?.name || "프로젝트"}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[12px] text-white/70">
                      <span>
                        {docKey ? (MACHINE_TITLES[docKey as MachineKey] ?? docKey) : "설비"}
                      </span>
                      <span className="rounded bg-blue-500/90 px-1.5 py-0.5 text-[11px] font-medium text-white">
                        {machineDocument?.title?.match(/#\d+/)?.[0] || `#${currentIndex + 1}`}
                      </span>
                    </span>
                  </>
                )}
              </div>
            </div>
            {/* 헤더 우측: 네비게이션 */}
            <div className="flex items-center gap-2">
              {/* 문서 네비게이션 */}
              {totalCount > 1 && (
                <div className="flex items-center gap-0.5 rounded-full bg-white/10 px-1 py-1">
                  <button
                    onClick={() => handleDocumentNavigation(-1)}
                    disabled={!hasPrev}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 active:bg-white/10 disabled:opacity-30"
                    aria-label="이전 문서"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <span className="min-w-[2.5rem] text-center text-[12px] font-medium tabular-nums text-white/90">
                    {currentIndex + 1} / {totalCount}
                  </span>
                  <button
                    onClick={() => handleDocumentNavigation(1)}
                    disabled={!hasNext}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 active:bg-white/10 disabled:opacity-30"
                    aria-label="다음 문서"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 탭 바 - 토스 스타일 언더라인 */}
        <div className="bg-white border-b border-gray-100">
          <div className="flex">
            <TabButton active={activeTab === "summary"} onClick={() => scrollToTab("summary")}>
              요약
            </TabButton>
            <TabButton active={activeTab === "checklist"} onClick={() => scrollToTab("checklist")}>
              점검
            </TabButton>
            <TabButton
              active={activeTab === "other-photos"}
              onClick={() => scrollToTab("other-photos")}
            >
              사진대지
            </TabButton>
            <TabButton active={activeTab === "measure"} onClick={() => scrollToTab("measure")}>
              계측
            </TabButton>
          </div>
        </div>
      </div>

      {/* 탭 콘텐츠 (transform 기반 슬라이딩 - 각 탭 독립 스크롤) */}
      <div ref={scrollContainerRef} className="relative min-h-0 flex-1 overflow-hidden">
        {/* 요약 탭 */}
        <div
          className="absolute inset-0 overflow-y-auto transition-transform duration-300 ease-out"
          style={{ transform: `translateX(${(0 - TAB_ORDER.indexOf(activeTab)) * 100}%)` }}
        >
          <SummaryTab
            machineDocument={localMachineInfo}
            docKey={docKey || ""}
            machineIndex={currentIndex}
            onFieldChange={handleMachineInfoChange}
            onBlur={handleMachineInfoBlur}
            isLoading={isDataStale}
            isSaving={savingMachineInfo}
            saveError={machineInfoError}
          />
        </div>

        {/* 점검 탭 - 자체 세로 스크롤 */}
        <div
          className="absolute inset-0 overflow-y-auto transition-transform duration-300 ease-out"
          style={{ transform: `translateX(${(1 - TAB_ORDER.indexOf(activeTab)) * 100}%)` }}
        >
          <ChecklistTab
            items={localCheckList}
            onChange={handleChecklistChange}
            onPhotoChange={handlePhotoChange}
            isLoading={isDataStale}
            savingItemId={savingItemId}
            projectId={projectId || ""}
            docKey={docKey || ""}
            machineId={machineId || ""}
          />
        </div>

        {/* 사진대지 탭 - 사진 개수에 따라 자체 세로 스크롤 */}
        <div
          className="absolute inset-0 overflow-y-auto transition-transform duration-300 ease-out"
          style={{ transform: `translateX(${(2 - TAB_ORDER.indexOf(activeTab)) * 100}%)` }}
        >
          <OtherPhotosTab
            pictures={localPictures}
            version={documentVersion}
            projectId={projectId || ""}
            docKey={docKey || ""}
            machineId={machineId || ""}
            onUpdate={handlePicturesUpdate}
            isLoading={isDataStale}
          />
        </div>

        {/* 계측 탭 */}
        <div
          className="absolute inset-0 overflow-y-auto transition-transform duration-300 ease-out"
          style={{ transform: `translateX(${(3 - TAB_ORDER.indexOf(activeTab)) * 100}%)` }}
        >
          <MeasureTab
            projectId={projectId || ""}
            docKey={docKey || ""}
            machineId={machineId || ""}
            isLoading={loading}
            onSubTabInfo={handleSubTabInfo}
            resetSubTabRef={resetMeasureSubTabRef}
          />
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative flex-1 py-3 text-[15px] font-medium transition-colors ${
        active ? "text-gray-900" : "text-gray-400 active:text-gray-600"
      }`}
    >
      {children}
      {active && (
        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-12 bg-gray-900 rounded-full" />
      )}
    </button>
  );
}
